import { Chip, useOverlayState } from "@heroui/react";
import { isAxiosError } from "axios";
import { Pencil, Trash } from "lucide-react";
import { useMemo, useState } from "react";

import { DeleteSupplierModal } from "./delete";
import { SupplierSheet } from "./form";

import {
  DataTable,
  RowMenu,
  useClientTable,
  type DataGridColumn,
} from "@/components/kit";
import { formatDay, formatDateTime } from "@/utils/date";
import { Supplier } from "@/types/supplier";
import { useDeleteSupplier } from "@/hooks/api/supplier";

interface SupplierTableProps {
  suppliers: Supplier[];
  isLoading: boolean;
}

export const SuppliersTable = ({
  suppliers,
  isLoading,
}: SupplierTableProps) => {
  const editState = useOverlayState();
  const deleteState = useOverlayState();
  const {
    mutate: deleteSupplier,
    isPending: isDeletingSupplier,
    error: deleteError,
    reset: resetDelete,
  } = useDeleteSupplier();

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null,
  );

  const handleDelete = (id: string) => {
    deleteSupplier(
      { id },
      {
        onSuccess: () => {
          deleteState.close();
          setSelectedSupplier(null);
        },
      },
    );
  };

  const deleteMessage = deleteError
    ? `Could not delete this supplier. ${
        (isAxiosError(deleteError) && deleteError.response?.data?.message) ||
        "It may still have products. Try again once they are moved."
      }`
    : null;

  const columns = useMemo<DataGridColumn<Supplier>[]>(
    () => [
      {
        id: "name",
        header: "Supplier",
        isRowHeader: true,
        allowsSorting: true,
        sortFn: (a, b) => a.name.localeCompare(b.name),
        cell: (supplier) => (
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate font-medium">{supplier.name}</span>
            <span className="text-muted truncate font-mono text-xs">
              {supplier.slug}
            </span>
          </div>
        ),
      },
      {
        id: "margin",
        header: "Margin",
        align: "end",
        width: 120,
        allowsSorting: true,
        sortFn: (a, b) => parseFloat(a.margin) - parseFloat(b.margin),
        cell: (supplier) => (
          <span className="tabular-nums">
            {parseFloat(supplier.margin).toFixed(2)}%
          </span>
        ),
      },
      {
        id: "cap",
        header: "Margin cap",
        align: "end",
        width: 150,
        allowsSorting: true,
        sortFn: (a, b) => parseFloat(a.cap) - parseFloat(b.cap),
        cell: (supplier) => (
          <span className="tabular-nums">
            AED {parseFloat(supplier.cap).toFixed(2)}
          </span>
        ),
      },
      {
        id: "updatedAt",
        header: "Updated",
        width: 210,
        allowsSorting: true,
        sortFn: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
        cell: (supplier) => (
          <div className="flex flex-col leading-tight">
            <span className="tabular-nums">
              {formatDateTime(supplier.updatedAt)}
            </span>
            <span className="text-muted text-xs tabular-nums">
              Created {formatDay(supplier.createdAt)}
            </span>
          </div>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        width: 130,
        allowsSorting: true,
        sortFn: (a, b) => Number(b.isActive) - Number(a.isActive),
        cell: (supplier) => (
          <Chip
            color={supplier.isActive ? "success" : "default"}
            variant="soft"
          >
            {supplier.isActive ? "Active" : "Inactive"}
          </Chip>
        ),
      },
      {
        id: "id",
        header: "ID",
        width: 170,
        cell: (supplier) => (
          <span className="text-muted block max-w-36 truncate font-mono text-xs">
            {supplier.id}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        width: 64,
        align: "end",
        cell: (supplier) => (
          <RowMenu
            items={[
              {
                id: "edit",
                label: "Edit",
                icon: <Pencil className="size-4" />,
                onAction: () => {
                  setSelectedSupplier(supplier);
                  editState.open();
                },
              },
              {
                id: "delete",
                label: "Delete",
                danger: true,
                icon: <Trash className="size-4" />,
                onAction: () => {
                  resetDelete();
                  setSelectedSupplier(supplier);
                  deleteState.open();
                },
              },
            ]}
            label={`Actions for ${supplier.name}`}
          />
        ),
      },
    ],
    [],
  );

  const table = useClientTable(suppliers, {
    columns,
    initialSort: { column: "name", direction: "ascending" },
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable
        compact
        columns={columns}
        empty={{
          title: "No suppliers yet",
          description: "Add a supplier to start importing their products.",
        }}
        getRowId={(supplier) => supplier.id}
        isLoading={isLoading && suppliers.length === 0}
        label="Suppliers Table"
        pagination={suppliers.length > 12 ? table.pagination : undefined}
        rows={table.rows}
        sort={table.sort}
        onSortChange={table.onSortChange}
      />

      <SupplierSheet
        isOpen={editState.isOpen}
        supplier={selectedSupplier}
        onClose={editState.close}
      />
      <DeleteSupplierModal
        error={deleteMessage}
        isDeleting={isDeletingSupplier}
        isOpen={deleteState.isOpen}
        supplier={selectedSupplier!}
        onDelete={handleDelete}
        onOpenChange={(open) =>
          open ? deleteState.open() : deleteState.close()
        }
      />
    </div>
  );
};
